import { useMutation } from "react-query";
import { CreateVacancy } from "../../models/vacancy/vacancy";
import { useNavigate } from "react-router-dom";
import { SubstituteSchedulerLesson } from "../../components/scheduler/SubstituteScheduler";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../auth/permissions";
import useAuthData from "../../auth/useAuthData";
import { useGlobalState } from "../../states/global-state/global.state";
import VacancyEditWizard, {
  VacancyFormFields,
} from "../vacancy/VacancyCreateWizard";
import FullScreenContainer from "../../components/FullScreenContainer";
import { useVacancyApi } from "../../apis/vacancy-api";
import { VacancyLesson } from "../../models/schedule/lesson";

function VacancyCreate() {
  const { hasPermissions } = usePermissions();
  const { currentTeacher: teacher, currentSchool: school } = useAuthData();
  const navigate = useNavigate();
  const { setIsLoading } = useGlobalState();
  const { createVacancy, getVacancy, updateSubstitutesOnVacancyLessons } =
    useVacancyApi();

  const onCreateVacancy = useMutation({
    mutationFn: (v: CreateVacancy) => createVacancy(v, school!.id),
  });

  const onSubmit = async (data: VacancyFormFields) => {
    const vacancy: CreateVacancy = {
      absentTeacherEmail: data.teacher.id,
      start: new Date(data.start),
      end: new Date(data.end),
      scheduleId: data.scheduleId,
      description: data.description,
      lessons: data.lessons.map((l) => VacancyLesson.toCreateVacancyLesson(l)),
      absenceReason: data.absenceReason,
    };
    setIsLoading(true);
    await onCreateVacancy
      .mutateAsync(vacancy)
      .then((vacancyId) => getVacancy(vacancyId))
      .then((vacancy) => {
        if (data.substitutes?.length) {
          updateSubstitutesOnVacancyLessons(
            vacancy,
            data.substitutes,
            data.lessons as SubstituteSchedulerLesson[],
            hasPermissions([PERMISSIONS.CanAcceptTeacherOnVacancy]) === false &&
              vacancy.absentTeacher.id !== teacher?.id,
          );
        }
      })
      .then(() =>
        setTimeout(() => {
          navigate(-1);
        }, 500),
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <FullScreenContainer className="flex bg-nid_grey p-4 md:p-8">
      {school && (
        <VacancyEditWizard
          school={school}
          preselectedTeacher={
            hasPermissions([PERMISSIONS.CanSelectAbsentTeacherOnCreateVacancy])
              ? undefined
              : teacher
          }
          hideTeacherStep={
            !hasPermissions([PERMISSIONS.CanSelectAbsentTeacherOnCreateVacancy])
          }
          onSubmit={onSubmit}
        ></VacancyEditWizard>
      )}
    </FullScreenContainer>
  );
}

export default VacancyCreate;
